

import { apiUrl } from './utils/apiBase';

// 🔍 Balance Verifier API - Multi-Token Onchain vs FIFO
export async function fetchBalanceVerify(payload) {
  try {
    const response = await fetch(apiUrl('/api/balance-verifier'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    
    const json = await response.json();
    
    if (!response.ok || !json?.ok) {
      throw new Error(json?.error || 'Balance verifier failed');
    }
    
    return json;
  } catch (error) {
        throw error;
  }
}

// 🚀 NEU: Dataset/WalletSet API Functions
export async function fetchDatasets(year = null) {
  try {
    const url = year ? apiUrl(`/api/datasets?year=${year}`) : apiUrl('/api/datasets');
    const response = await fetch(url, {
      credentials: 'include'
    });
    
    // 🚀 FIX: 401 explizit behandeln (fehlendes JWT-Cookie)
    if (response.status === 401) {
      return { unauthorized: true, success: false };
    }
    
    if (!response.ok) {
      throw new Error(`Failed to fetch datasets: HTTP ${response.status}`);
    }
    
    const data = await response.json();
    
    // ✅ Normalisiere Properties zu snake_case (für konsistente Verwendung im Frontend)
    if (data.success && data.datasets) {
      data.datasets = data.datasets.map(d => ({
        ...d,
        snapshot_exists: d.snapshot_exists ?? d.snapshotExists ?? false,
        last_calculated_at: d.last_calculated_at ?? d.lastCalculatedAt ?? null,
      }));
    }
    
    return data;
  } catch (error) {
        return { success: false, error: error.message };
  }
}

export async function fetchDatasetStatus(datasetId) {
  try {
    const response = await fetch(apiUrl(`/api/datasets/${datasetId}/status`), {
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch dataset status');
    }
    
    return await response.json();
  } catch (error) {
        throw error;
  }
}

export async function deleteDatasetSnapshot(datasetId, year) {
  try {
    const response = await fetch(apiUrl(`/api/datasets/${datasetId}/snapshot?year=${year}`), {
      method: 'DELETE',
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error('Failed to delete dataset snapshot');
    }
    
    return await response.json();
  } catch (error) {
        throw error;
  }
}

export { saveNftTransformFmvOverrides } from './utils/nftTransformFmvOverrides.js';

export async function fetchManualOverrideSummary(datasetId, year) {
  const y = encodeURIComponent(String(year || "").trim());
  const response = await fetch(
    apiUrl(`/api/datasets/${datasetId}/overrides?year=${y}`),
    { credentials: "include" }
  );

  const json = await response.json().catch(() => null);

  if (!response.ok || json?.success === false) {
    throw new Error(json?.message || json?.error || `Failed to load manual overrides (${response.status})`);
  }

  return {
    count: Number(json?.count || 0),
    overrides: json?.overrides || {},
  };
}

export async function resetManualOverrides(datasetId, year) {
  const response = await fetch(apiUrl(`/api/datasets/${datasetId}/overrides`), {
    method: "DELETE",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ year: Number(year) }),
  });

  const json = await response.json().catch(() => null);

  if (!response.ok || json?.success === false) {
    throw new Error(json?.message || json?.error || `Failed to reset manual overrides (${response.status})`);
  }

  return json;
}

export async function syncDataset(datasetId) {
  try {
    const response = await fetch(apiUrl(`/api/datasets/${datasetId}/sync`), {
      method: 'POST',
      credentials: 'include'
    });

    let json = null;
    try {
      json = await response.json();
    } catch (e) {
      // kein/kaputter JSON-Body -> okay, json bleibt null
    }
    
    if (!response.ok) {
      const message = (json && json.error)
        ? json.error
        : `Failed to sync dataset (HTTP ${response.status})`;

      const error = new Error(message);
      error.status = response.status;
      error.body = json;
      throw error;
    }
    
    return json;
  } catch (error) {
        throw error;
  }
}

export async function calculateDataset(datasetId, year = '2025') {
  try {
    const response = await fetch(apiUrl(`/api/datasets/${datasetId}/calculate`), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({ year })
    });
    
    if (!response.ok) {
      throw new Error('Failed to calculate dataset');
    }
    
    return await response.json();
  } catch (error) {
        throw error;
  }
}

export async function fetchDatasetAnalysis(datasetId, walletFilter = null) {
  try {
    const url = walletFilter 
      ? apiUrl(`/api/datasets/${datasetId}/analysis?wallet=${walletFilter}`)
      : apiUrl(`/api/datasets/${datasetId}/analysis`);
      
    const response = await fetch(url, {
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch dataset analysis');
    }
    
    return await response.json();
  } catch (error) {
        throw error;
  }
}

export async function fetchWalletData(address) {
  try {
    const urls = {
      balance: `/api/account/vet-vtho?address=${address}`,
      txin: `/api/account/txin?address=${address}`,
      txout: `/api/account/txout?address=${address}`,
      tokenTransfers: `/api/account/token-transfers?address=${address}`,
    };

    const [balanceRes, txinRes, txoutRes, tokenRes] = await Promise.all([
      fetch(urls.balance),
      fetch(urls.txin),
      fetch(urls.txout),
      fetch(urls.tokenTransfers),
    ]);

    if (!balanceRes.ok || !txinRes.ok || !txoutRes.ok || !tokenRes.ok) {
      throw new Error("Mindestens eine API-Anfrage ist fehlgeschlagen.");
    }

    const balanceData = await balanceRes.json();
    const txinData = await txinRes.json();
    const txoutData = await txoutRes.json();
    const tokenData = await tokenRes.json();

    return {
      vet: {
        balance: balanceData.data?.vet || 0,
        received: Array.isArray(txinData.data) ? txinData.data : [],    // Eingänge
        sent: Array.isArray(txoutData.data) ? txoutData.data : [],      // Ausgänge
      },
      vtho: {
        balance: balanceData.data?.vtho || 0,
        transfers: Array.isArray(tokenData.data)
          ? tokenData.data.filter(tx => tx.token?.toUpperCase() === "VTHO")
          : [],
      },
      tokens: {
        allTransfers: Array.isArray(tokenData.data) ? tokenData.data : [],
      }
    };
  } catch (error) {
        throw error;
  }
}

// 🚀 NEU: Manuelle Krypto-Zahlungen (VET, b3tr, vtho)
export const cryptoPaymentAPI = {
  // Krypto-Checkout erstellen
  async createCryptoCheckout(plan, year, walletCount, walletAddresses, preferredToken = 'vet') {
    try {
      const response = await fetch('/api/crypto/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          plan,
          year,
          walletCount,
          walletAddresses,
          preferredToken
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
            throw error;
    }
  },

  // Krypto-Zahlungsstatus prüfen
  async checkCryptoPaymentStatus(paymentId) {
    try {
      const response = await fetch(`/api/crypto/payment-status/${paymentId}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
            throw error;
    }
  },

  // 🆕 NEU: Verfügbare Kryptowährungen abrufen
  async getAvailableCurrencies() {
    try {
      const response = await fetch('/api/crypto/currencies', {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
            throw error;
    }
  },

  // 🆕 NEU: Blockchain-Überwachung starten
  async startBlockchainMonitoring(paymentId, expectedAmount, expectedToken) {
    try {
      const response = await fetch(`/api/crypto/start-monitoring/${paymentId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ expectedAmount, expectedToken })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
            throw error;
    }
  },

  // 🆕 NEU: Blockchain-Überwachung stoppen
  async stopBlockchainMonitoring(paymentId) {
    try {
      const response = await fetch(`/api/crypto/stop-monitoring/${paymentId}`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
            throw error;
    }
  },

  // 🆕 NEU: Blockchain-Überwachungsstatus abrufen
  async getBlockchainMonitoringStatus() {
    try {
      const response = await fetch('/api/crypto/monitoring-status', {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
            throw error;
    }
  }
};

// 🎯 NEU: Snapshot-Management API-Funktionen (angepasst an neues Dataset-System!)
export const snapshotAPI = {
  // Lade gespeicherte Berechnung (über Dataset-ID)
  async loadCalculation(datasetId, year) {
    try {
      const response = await fetch(apiUrl(`/api/datasets/${datasetId}/analysis?year=${year}`), {
        credentials: 'include'
      });

      if (!response.ok) {
        if (response.status === 404) {
          return { success: false, message: 'No calculation found' };
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { success: true, calculation: data.analysisResult };
    } catch (error) {
            return { success: false, message: error.message };
    }
  },

  // Speichere neue Berechnung (über Dataset-ID)
  async saveCalculation(datasetId, year, calculationData, delegateWallet = null, userInputs = {}) {
    try {
      const response = await fetch(apiUrl(`/api/datasets/${datasetId}/calculate`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          year,
          selectedCountry: 'DE'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { success: true, analysisResult: data.analysisResult };
    } catch (error) {
            return { success: false, message: error.message };
    }
  },

  // Update Berechnungsstatus (für Sync)
  async updateCalculationStatus(walletAddress, year, status) {
    try {
      const response = await fetch(apiUrl(`/api/wallet-calculations-pg/${walletAddress}/${year}/status`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ status })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
            return { success: false, message: error.message };
    }
  },

  // Update User-Inputs (für Bearbeitungsmodus)
  async updateUserInputs(walletAddress, year, userInputs) {
    try {
      const response = await fetch(apiUrl(`/api/wallet-calculations-pg/${walletAddress}/${year}/user-inputs`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ userInputs })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
            return { success: false, message: error.message };
    }
  },

  // Lösche Berechnung
  async deleteCalculation(walletAddress, year) {
    try {
      const response = await fetch(apiUrl(`/api/wallet-calculations-pg/${walletAddress}/${year}`), {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
            return { success: false, message: error.message };
    }
  }
};

// 🚀 NEUE FUNKTIONEN: Automatische Transaktionsvorbereitung für VeWorld Wallet
export const veWorldPaymentAPI = {
  // 🎯 Automatische Transaktion für VeWorld Wallet vorbereiten
  prepareTransaction: async (plan, year, walletCount, preferredToken) => {
    try {
      // 🚀 WICHTIG: Backend-API für echte Preise verwenden (macht echte VeChainStats API-Calls!)
      try {
        const response = await fetch(apiUrl('/api/crypto/prepare-transaction'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            plan,
            year,
            walletCount,
            preferredToken
          }),
          credentials: 'include'
        });

        if (response.ok) {
          const backendData = await response.json();
                    return backendData;
        } else if (response.status === 401) {
                    throw new Error('Backend authentication failed');
        } else {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Transaktionsvorbereitung fehlgeschlagen');
        }
      } catch (backendError) {
                
        // 🎯 Fallback: Lokale Auth mit geschätzten Preisen (nur wenn Backend nicht verfügbar)
        const localAuth = localStorage.getItem('v3tax_auth');
        if (localAuth) {
          try {
            const authData = JSON.parse(localAuth);
            if (authData.authenticated && authData.walletAddress) {
                            
              // 🎯 Fallback: Geschätzte Preise für lokale Auth
              const planPrices = {
                'Free': 0,
                'Standard': 39.99,
                'Pro': 59.99,
                'Elite': 79.99,
                'Ultimate': 109.99,
              };
              
              const priceEUR = planPrices[plan] || 0;
              
              // 🚀 Geschätzte Token-Preise (nur als Fallback!)
              const estimatedTokenPrices = {
                'vet': 0.02,
                'vtho': 0.0001,
                'b3tr': 0.0001
              };
              
              const tokenPrice = estimatedTokenPrices[preferredToken.toLowerCase()] || 0.01;
              const tokenAmount = priceEUR / tokenPrice;
              
              const mockTransaction = {
                success: true,
                sessionId: `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                transactionData: {
                  plan: plan,
                  year: year,
                  walletCount: walletCount,
                  token: preferredToken,
                  tokenSymbol: preferredToken.toUpperCase(),
                  value: tokenAmount,
                  to: '0xa25f5ae9dce5c4f136eac6e56a73381a61ee39a1',
                  userWallet: authData.walletAddress, // 🚀 WICHTIG: userWallet hinzugefügt
                  walletAddress: authData.walletAddress,
                  isLocalAuth: true,
                  priceEUR: priceEUR,
                  priceToken: tokenPrice
                },
                isLocalAuth: true
              };
              
                            return mockTransaction;
            }
          } catch (parseError) {
                      }
        }
        
        throw new Error('Backend nicht verfügbar und keine lokale Auth gefunden');
      }
    } catch (error) {
            throw error;
    }
  },

  // 🚀 NEU: Upgrade-Transaktionsvorbereitung
  prepareUpgradeTransaction: async (fromPlan, toPlan, year, preferredToken) => {
    try {
      const response = await fetch(apiUrl('/api/crypto/prepare-upgrade'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fromPlan,
          toPlan,
          year,
          preferredToken
        }),
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Upgrade-Transaktionsvorbereitung fehlgeschlagen');
      }

      const data = await response.json();
            return data;
    } catch (error) {
            throw error;
    }
  },

  // 🔍 Transaktionsstatus abfragen
  checkTransactionStatus: async (sessionId) => {
    try {
      const response = await fetch(apiUrl(`/api/crypto/transaction-status/${sessionId}`), {
        method: 'GET',
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Status-Abfrage fehlgeschlagen');
      }

      return await response.json();
    } catch (error) {
            throw error;
    }
  },

  // 🚀 ECHTE VeWorld Wallet Integration - Direkte Transaktionen!
  openVeWorldTransaction: async (transactionData) => {
    // 🚀 KRITISCH: paymentId einmal robust auflösen (aus Param oder als Fallback aus localStorage)
    const resolvedPaymentId =
      (transactionData && transactionData.paymentId) ||
      localStorage.getItem('currentPaymentId') ||
      null;
    
        if (!resolvedPaymentId) {
      throw new Error('PaymentId fehlt – Pending-Order wurde nicht korrekt initialisiert.');
    }
    
    // 🚀 KRITISCH: Debug-Log direkt am Anfang der Funktion
                
    // 🎯 HELPER: Versuche VeWorld App zu öffnen (Android Intent + Universal Link)
    const openWalletOnMobile = async (uri, transactionData) => {
      try {
                
        // 🚀 WalletConnect Universal Link (funktioniert mit vielen Wallets)
        const walletConnectUniversal = `https://walletconnect.com/wc?uri=${encodeURIComponent(uri)}`;
        
        // 🚀 Android Intent (falls VeWorld App installiert ist)
        const androidIntent = `intent://wc?uri=${encodeURIComponent(uri)}#Intent;package=org.vechain.veworld.app;scheme=wc;end`;
        
        // 🎯 1. Versuch: Android Intent (funktioniert nur auf Android mit installierter App)
        if (/Android/i.test(navigator.userAgent)) {
                    
          // Visibility Trick: Detect if app opened
          let hidden = false;
          const onVis = () => { hidden = document.hidden; };
          document.addEventListener('visibilitychange', onVis);
          
          // Versuche Intent
          window.location.href = androidIntent;
          
          // Warte kurz und prüfe ob App geöffnet wurde
          setTimeout(() => {
            document.removeEventListener('visibilitychange', onVis);
            if (!hidden) {
                            return { success: true, message: 'VeWorld App geöffnet via Android Intent' };
            } else {
                            // Fallback: Universal Link
              window.location.href = walletConnectUniversal;
              return { success: true, message: 'VeWorld App geöffnet via Universal Link' };
            }
          }, 1200);
          
        } else {
          // 🎯 2. Versuch: Universal Link (funktioniert auf allen Plattformen)
                    window.location.href = walletConnectUniversal;
          return { success: true, message: 'VeWorld App geöffnet via Universal Link' };
        }
        
      } catch (error) {
                return { success: false, message: 'App konnte nicht geöffnet werden' };
      }
    };
    try {
            
      // 🎯 1. PRIORITÄT: VeWorld Browser Extension (direkte Transaktion)
                              
      // 🚀 Versuche nur echte VeWorld Provider (kein MetaMask!)
      const veWorldProviders = [
        window.veworld,
        window.vechain
      ].filter(Boolean);
      
      // 🚨 WICHTIG: Ignoriere MetaMask komplett
      if (window.ethereum && !window.ethereum.isVeWorld && !window.ethereum.isVeChain) {
              }
      
      if (veWorldProviders.length > 0) {
                
        for (const provider of veWorldProviders) {
          try {
                        
            // 🚀 Prüfe ob Provider VeChain-Methoden unterstützt
            let params = {
              from: transactionData.userWallet,
              to: transactionData.to,
              value: transactionData.value,
              gas: transactionData.gas,
              gasPrice: transactionData.gasPrice
            };
            
            // 🎯 Versuche die korrekten VeWorld-API-Methoden
            try {
                            
              // 🚀 1. Versuch: VeWorld Connex API (Standard für VeChain)
              if (provider.newConnex) {
                                
                try {
                  // 🎯 Korrekte VeWorld Connex API
                  const connex = provider.newConnex({
                    node: 'https://mainnet.vechain.org',
                    network: 'main'
                  });
                  
                                    
                  // 🚀 Prüfe verfügbare Methoden
                                    
                  // 🎯 WICHTIG: Verwende die VeWorld Extension API direkt (newConnexVendor)!
                  // Das ist der korrekte Weg für echte VeWorld-Integration
                  if (window.vechain && window.vechain.newConnexVendor) {
                                        
                    try {
                      // 🚀 GPT5-OPTIMIERT: Token-Metadaten und Helfer-Funktionen
                      const TOKENS = {
                        VET: { type: "native", decimals: 18 },
                        VTHO: {
                          type: "vip180",
                          address: "0x0000000000000000000000000000456E65726779",
                          defaultDecimals: 18, // 🚨 WICHTIG: VTHO hat 18 Dezimalstellen, nicht 6!
                        },
                        B3TR: {
                          type: "vip180",
                          address: "0x5ef79995FE8a89e0812330E4378eB2660ceDe699",
                          defaultDecimals: 18,
                        },
                      };
                      
                      // 🚀 DATENBANK-INTEGRATION: VeWorld-Purchase in PostgreSQL speichern
                      async function saveVeWorldPurchaseToDatabase(purchaseData, paymentId) {
                        try {
                                                                              
                          if (!paymentId) {
                            throw new Error('PaymentId ist erforderlich für die Finalisierung!');
                          }
                          
                          // 🎯 NEU: API-Call an den neuen finalize Endpoint
                          const response = await fetch(`/api/purchases/finalize/${paymentId}`, {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json'
                            },
                            credentials: 'include', // 🚀 Verwende Cookies für Auth (wie bei Stripe)
                            body: JSON.stringify({
                              transactionHash: purchaseData.transactionHash
                            })
                          });
                          
                          if (!response.ok) {
                            const errorText = await response.text();
                                                        throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
                          }
                          
                          const result = await response.json();
                                                    
                          return result;
                          
                        } catch (error) {
                                                    throw error;
                        }
                      }
                      
                      const VIP180_TRANSFER_ABI = {
                        constant: false,
                        inputs: [
                          { name: "_to", type: "address" },
                          { name: "_amount", type: "uint256" },
                        ],
                        name: "transfer",
                        outputs: [{ name: "success", type: "bool" }],
                        payable: false,
                        stateMutability: "nonpayable",
                        type: "function",
                      };
                      
                      // 🚀 GPT5-Helper: Robuste String->Wei Konvertierung
                      function toBaseUnitString(amountStr, decimals) {
                        const [ints, fracs = ""] = amountStr.trim().split(".");
                        const fracPadded = (fracs + "0".repeat(decimals)).slice(0, decimals);
                        const bi = (BigInt(ints || "0") * (10n ** BigInt(decimals))) + BigInt(fracPadded || "0");
                        return bi.toString();
                      }
                      
                      // 🚀 1. Hole die Genesis-ID aus der Extension (vermeidet Mismatch!)
                                            const genesisId = await connex.thor.genesis.id;
                                            
                      // 🚀 2. Erstelle Vendor - WICHTIG: Kein Objekt, sondern String oder gar nichts!
                      let vendor;
                      try {
                        // 🎯 Versuch 1: Ohne Parameter (auto-bind auf Extension-Netz)
                                                vendor = window.vechain.newConnexVendor();
                                              } catch (error) {
                        // 🎯 Versuch 2: Mit String direkt (nicht als Objekt!)
                                                vendor = window.vechain.newConnexVendor(genesisId); // String direkt!
                                              }
                      
                      // 🚀 3. GPT5-OPTIMIERT: Einheitlicher Token-Transfer-Flow
                      const tokenSymbol = transactionData.token.toUpperCase();
                      const meta = TOKENS[tokenSymbol];
                      
                      if (!meta) {
                        throw new Error(`Unbekanntes Asset: ${tokenSymbol}`);
                      }
                      
                                            
                      // 🎯 Konvertiere zu Base Units mit korrekten Dezimalstellen
                      const decimals = meta.decimals || meta.defaultDecimals;
                      const amountWei = toBaseUnitString(transactionData.value, decimals);
                      
                                                                  
                      // 🎯 WICHTIG: Prüfe Empfänger-Typ vor Transaktion
                      try {
                                                const accountInfo = await connex.thor.account(transactionData.to).get();
                        const isContract = accountInfo && accountInfo.hasCode;
                        
                                                
                        if (isContract) {
                                                  }
                      } catch (accountError) {
                                              }
                      
                      // 🎯 WICHTIG: Simuliere Transaktion vor dem Senden (explain)
                      try {
                                                
                        if (transactionData.token === 'vet') {
                          // 🎯 VET-Transfer simulieren
                          const exp = await connex.thor.explain([{
                            to: transactionData.to,
                            value: amountWei,
                            data: '0x'
                          }]).execute();
                          
                                                    
                          // 🎯 Prüfe ob Transfer korrekt erkannt wird
                          if (exp && exp[0] && exp[0].transfers && exp[0].transfers.length > 0) {
                            const transfer = exp[0].transfers[0];
                                                      } else {
                                                      }
                          
                        } else if (transactionData.token === 'vtho') {
                          // 🎯 VTHO-Transfer simulieren
                          const vthoContractAddress = '0x0000000000000000000000000000456E65726779';
                          const transferSignature = 'a9059cbb';
                          const recipientPadded = transactionData.to.slice(2).padStart(64, '0');
                          const valuePadded = BigInt(amountWei).toString(16).padStart(64, '0');
                          const transferData = '0x' + transferSignature + recipientPadded + valuePadded;
                          
                          const exp = await connex.thor.explain([{
                            to: vthoContractAddress,
                            value: '0',
                            data: transferData
                          }]).execute();
                          
                                                    
                          // 🎯 Prüfe ob VTHO-Transfer korrekt erkannt wird
                          if (exp && exp[0] && exp[0].transfers && exp[0].transfers.length > 0) {
                            const transfer = exp[0].transfers[0];
                                                      } else {
                                                      }
                        }
                        
                      } catch (explainError) {
                                              }
                      
                      // 🚀 4. GPT5-OPTIMIERT: Erstelle Clauses basierend auf Token-Typ
                      let clauses = [];
                      let tokenAddress = undefined;
                      
                      if (meta.type === "native") {
                        // 🎯 VET: Native Token (direkte Übertragung)
                        clauses = [{
                          to: transactionData.to,
                          value: amountWei,
                          data: '0x'
                        }];
                        
                        // 🚀 WICHTIG: Kommentar für VET hinzufügen (wie bei VTHO/B3TR)
                        clauses[0].comment = `Transfer ${transactionData.value} ${tokenSymbol}`;
                        
                                                                        
                      } else if (meta.type === "vip180") {
                        // 🎯 VTHO/B3TR: VIP-180 Token (Contract Call über ABI)
                        tokenAddress = meta.address;
                        
                                                
                        // 🚀 GPT5-Weg: Verwende Connex ABI-basierte Methode
                        const method = connex.thor.account(tokenAddress).method(VIP180_TRANSFER_ABI);
                        const clause = method.asClause(transactionData.to, amountWei);
                        
                        // 🎯 Optionaler Kommentar im Wallet
                        clause.comment = `Transfer ${transactionData.value} ${tokenSymbol}`;
                        
                        clauses = [clause];
                        
                                                                        
                      } else {
                        throw new Error(`Unbekannter Token-Typ: ${meta.type}`);
                      }
                      
                                                                                                              
                      // 🚀 5. Vendor-Flow starten (das ist der korrekte Weg!)
                      const signing = vendor
                        .sign('tx', clauses)
                        .comment(`V3Tax ${transactionData.plan} Plan - ${transactionData.value} ${tokenSymbol}`);
                      
                      // 🚀 FIX: Payment-Memo als zusätzliche Sicherheit
                      // TODO: paymentId wird später als Memo hinzugefügt, wenn verfügbar
                      
                                                                                                                                                                                                      
                      // 🕐 SESSION TIMER: Automatischer Abbruch nach 5 Minuten
                      const SESSION_TIMEOUT = 300000; // 5 Minuten in ms
                      let sessionExpired = false;
                      
                      // 🚀 PROGRESS TIMER: Zeigt verbleibende Zeit alle 30 Sekunden
                      const PROGRESS_INTERVAL = 30000; // 30 Sekunden
                      let timeRemaining = SESSION_TIMEOUT;
                      
                      const progressTimer = setInterval(() => {
                        timeRemaining -= PROGRESS_INTERVAL;
                        const minutesLeft = Math.ceil(timeRemaining / 60000);
                        const secondsLeft = Math.ceil((timeRemaining % 60000) / 1000);
                        
                        if (timeRemaining > 0) {
                                                  }
                      }, PROGRESS_INTERVAL);
                      
                      const sessionTimer = setTimeout(async () => {
                                                sessionExpired = true;
                        clearInterval(progressTimer); // Progress Timer stoppen
                        
                        // 🚀 HINWEIS: Pending Order wird erst beim Klick auf "Transaktion starten" erstellt
                      }, SESSION_TIMEOUT);
                      
                                                                  
                      // 🚀 6. Transaktion anfordern (User muss unterschreiben!)
                      let result;
                      try {
                        result = await signing.request();
                        
                        // ✅ Timer stoppen wenn Transaktion erfolgreich gestartet
                        clearTimeout(sessionTimer);
                        clearInterval(progressTimer);
                                                                        
                      } catch (signingError) {
                        // ❌ Timer stoppen bei Fehlern
                        clearTimeout(sessionTimer);
                        clearInterval(progressTimer);
                                                                        
                        // 🚀 HINWEIS: Pending Order wird erst beim Klick auf "Transaktion starten" erstellt
                        
                        if (sessionExpired) {
                          throw new Error('Transaktion abgebrochen: Session Timeout (5 Minuten überschritten)');
                        } else {
                          throw signingError;
                        }
                      }
                      
                                            
                      // 🎯 WICHTIG: Transaktions-Hash extrahieren und Status verfolgen
                      let txHash = null;
                      if (result && result.txid) {
                        txHash = result.txid;
                                              } else if (result && result.hash) {
                        txHash = result.hash;
                                              } else {
                                              }
                      
                      // 🚀 HINWEIS: Pending Order wird jetzt in App.jsx beim Klick auf "Transaktion starten" erstellt
                                            
                      // 🚀 Transaktions-Status verfolgen (falls Hash verfügbar)
                      if (txHash) {
                        try {
                                                    
                          // 🎯 WICHTIG: Verwende Receipt-Polling statt Event-Listening (GPT5-Empfehlung)
                                                    
                          // 🚀 Robuster Receipt-Polling mit Timeout
                          async function waitReceipt(id, { retries = 30, delay = 2000 } = {}) {
                                                        
                            for (let i = 0; i < retries; i++) {
                              try {
                                                                
                                // 🎯 WICHTIG: Verwende getReceipt() statt get() für korrekte Erfolgs-Bestätigung
                                const receipt = await connex.thor.transaction(id).getReceipt();
                                
                                if (receipt) {
                                                                    return receipt;
                                }
                                
                                                                await new Promise(resolve => setTimeout(resolve, delay));
                                
                              } catch (receiptError) {
                                                                
                                if (i === retries - 1) {
                                  throw new Error(`Timeout: Keine Receipt nach ${retries} Versuchen erhalten`);
                                }
                                
                                await new Promise(resolve => setTimeout(resolve, delay));
                              }
                            }
                            
                            throw new Error(`Timeout: Keine Receipt nach ${retries} Versuchen erhalten`);
                          }
                          
                      // 🎯 KRITISCH: Hole wallet_pairs direkt aus localStorage (MIT Transaktionsdaten!)
                      const lsPairs = JSON.parse(localStorage.getItem('wallet_pairs') || '[]');
                      const walletAddresses = lsPairs.map(p => ({
                        main_wallet: p.main_wallet,
                        delegate_wallet: p.delegate_wallet || null,
                        label: p.label || null,
                        chain: p.chain || 'vechain',
                        // 🎯 KRITISCH: Zähler & Mengen mitgeben!
                        main_tx_count: Number(p.main_tx_count || 0),
                        delegate_tx_count: Number(p.delegate_tx_count || 0),
                        vet_amount: Number(p.vet_amount || 0),
                        vtho_amount: Number(p.vtho_amount || 0),
                        b3tr_amount: Number(p.b3tr_amount || 0),
                        nft_count: Number(p.nft_count || 0)
                      }));
                      
                      const walletCount = walletAddresses.length || transactionData.walletCount || 1;
                      
                                                                                        
                      // 🚀 WICHTIG: Verwende resolvedPaymentId (wurde am Anfang der Funktion aufgelöst)
                                                                  
                      // 🚀 Warte auf Receipt
                      const receipt = await waitReceipt(txHash, { retries: 30, delay: 2000 });
                          
                          // 🎯 GPT5-OPTIMIERT: Robustes Receipt-Parsing für alle Token-Typen
                                                    
                          if (receipt.reverted) {
                                                        
                            // 🎯 Zeige detaillierte Fehler-Informationen
                            if (receipt.outputs && receipt.outputs.length > 0) {
                              receipt.outputs.forEach((output, index) => {
                                                                if (output.vmError) {
                                                                  }
                              });
                            }
                            
                            const err = receipt.vmError || "Tx reverted";
                            throw new Error(`Transaktion reverted: ${err}`);
                            
                          } else {
                                                        
                            // 🚀 GPT5-Weg: Verifiziere Transfer basierend auf Token-Typ
                            let transferFound = false;
                            
                            if (meta.type === "native") {
                              // 🎯 VET: Native Transfers in outputs[].transfers[]
                                                            
                              for (const output of receipt.outputs || []) {
                                for (const transfer of output.transfers || []) {
                                  const okRecipient = transfer.recipient?.toLowerCase() === transactionData.to.toLowerCase();
                                  const okAmount = transfer.amount && transfer.amount.toString() === amountWei;
                                  
                                                                    
                                  if (okRecipient && okAmount) {
                                                                        transferFound = true;
                                    break;
                                  }
                                }
                                if (transferFound) break;
                              }
                              
                              if (!transferFound) {
                                                              }
                              
                            } else if (meta.type === "vip180") {
                              // 🎯 VTHO/B3TR: VIP-180 Transfer Events in outputs[].events[]
                                                                                          
                              const VIP180_TRANSFER_TOPIC = "0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef";
                              
                              for (const output of receipt.outputs || []) {
                                                                                                                                
                                for (const event of output.events || []) {
                                                                    const okAddress = event.address?.toLowerCase() === tokenAddress?.toLowerCase();
                                  const okTopic = event.topics?.[0] === VIP180_TRANSFER_TOPIC;
                                  
                                                                    
                                  if (okAddress && okTopic) {
                                    // topics[1] = from, topics[2] = to (rechts-gefüllt 32 Bytes)
                                    const topicTo = "0x" + event.topics[2].slice(26);
                                    const okTo = topicTo.toLowerCase() === transactionData.to.toLowerCase();
                                    const okAmount = event.data && BigInt(event.data).toString() === BigInt(amountWei).toString();
                                    
                                                                        
                                    if (okTo && okAmount) {
                                                                            transferFound = true;
                                      break;
                                    }
                                  }
                                }
                                if (transferFound) break;
                              }
                              
                              if (!transferFound) {
                                                              }
                            }
                            
                                                        
                            if (transferFound) {
                                                          } else {
                                                          }
                            
                            // 🚀 WICHTIG: Datenbank-Speicherung IMMER ausführen wenn Transaktion erfolgreich war!
                                                        try {
                                                            
                              // 🚀 WICHTIG: Hole die selected wallets aus dem localStorage!
                              const analyzedWallets = JSON.parse(localStorage.getItem('analyzedWallets') || '[]');
                                                            
                              // 🎯 GENAU WIE BEI STRIPE: Übergebe die kompletten analyzedWallets mit Paaren!
                                                            
                              // 🚀 WICHTIG: Hole userWallet - die EINGELOGGTE Wallet hat Priorität!
                              // PRIORITÄT 1: authData.walletAddress aus localStorage (die eingeloggte Wallet)
                              // PRIORITÄT 2: transactionData.userWallet (falls gesetzt)
                              // PRIORITÄT 3: gasPayer aus receipt (Fallback für eingeloggte Wallet)
                              // PRIORITÄT 4: main_wallet aus analyzedWallets (nur wenn nichts anderes verfügbar)
                              let userWallet = null;
                              
                              const authData = JSON.parse(localStorage.getItem('v3tax_auth') || '{}');
                              userWallet = authData.walletAddress || transactionData.userWallet || null;
                              
                              // Fallback: Verwende gasPayer (ist normalerweise die eingeloggte Wallet bei VeWorld)
                              if (!userWallet && receipt && receipt.gasPayer) {
                                userWallet = receipt.gasPayer;
                                                              }
                              
                              // Letzter Fallback: analyzedWallets[0].main_wallet
                              if (!userWallet && analyzedWallets && analyzedWallets.length > 0 && analyzedWallets[0].main_wallet) {
                                userWallet = analyzedWallets[0].main_wallet;
                                                              }
                              
                                                                                          
                              // 🚨 KRITISCH: userWallet MUSS gesetzt sein, sonst Fehler werfen!
                              if (!userWallet) {
                                                                                                                                                                                                throw new Error('UserWallet konnte nicht ermittelt werden - Purchase kann nicht gespeichert werden!');
                              }
                              
                              await saveVeWorldPurchaseToDatabase({
                                userWallet: userWallet, // 🚀 Verwende die Wallet-Adresse aus dem Auth-Context
                                plan: transactionData.plan,
                                year: transactionData.year,
                                walletCount: transactionData.walletCount,
                                walletAddresses: analyzedWallets, // 🎯 Übergebe die kompletten Objekte mit delegateWallet
                                paymentToken: tokenSymbol,
                                paymentAmount: transactionData.value,
                                transactionHash: txHash,
                                paymentMethod: 'veworld'
                              }, resolvedPaymentId); // 🚀 WICHTIG: Verwende resolvedPaymentId (wurde am Anfang aufgelöst)!
                              
                                                                                          
                            } catch (dbError) {
                                                            // 🚨 WICHTIG: Transaktion war erfolgreich, aber DB-Speicherung fehlgeschlagen
                                                          }
                            
                                                      }
                          
                        } catch (statusError) {
                                                    
                          // 🚨 WICHTIG: Zeige Fehler an User
                          if (statusError.message.includes('reverted')) {
                            throw new Error('Transaktion fehlgeschlagen: ' + statusError.message);
                          } else {
                                                      }
                        }
                      }
                      
                      return { 
                        method: 'direct', 
                        success: true, 
                        transaction: result,
                        txHash: txHash,
                        message: 'Transaktion gestartet - User muss unterschreiben!'
                      };
                      
                    } catch (vendorError) {
                                            
                      // 🎯 Fallback: Zeige bessere Fehlermeldung
                      if (vendorError.message.includes('user rejected')) {
                        throw new Error('Transaktion vom User abgelehnt');
                      } else if (vendorError.message.includes('not unlocked')) {
                        throw new Error('VeWorld Extension ist gesperrt - bitte entsperren');
                      } else if (vendorError.message.includes('Invalid genesisId')) {
                        throw new Error('VeWorld Extension Network-Konfiguration fehlerhaft - bitte Extension neu starten');
                      } else {
                        throw vendorError;
                      }
                    }
                  } else {
                                      }
                  
                                    
                } catch (connexError) {
                                  }
              }
              
              // 🚨 FALLBACK: Zeige Fehlermeldung für User
                            return {
                method: 'error',
                success: false,
                error: 'VeWorld Extension konnte nicht verwendet werden',
                message: 'Bitte stelle sicher, dass VeWorld installiert und entsperrt ist'
              };
              
              // 🚀 2. Versuch: VeWorld Signer API
              if (provider.newConnexSigner) {
                                
                try {
                  const signer = provider.newConnexSigner();
                                    
                  // 🚀 Transaktion unterschreiben
                  const result = await signer.sign(transactionData.to, transactionData.value);
                  
                                    return { 
                    method: 'direct', 
                    success: true, 
                    transaction: result,
                    message: 'Transaktion über VeWorld Signer API gestartet!'
                  };
                  
                } catch (signerError) {
                                  }
              }
              
              // 🚀 3. Versuch: Direkte VeWorld-API
                            
              if (provider.request && typeof provider.request === 'function') {
                try {
                  // 🎯 Versuche die echten VeWorld-Methoden
                  const methods = [
                    'vechain_sendTransaction',
                    'vechain_signTransaction',
                    'vechain_signMessage',
                    'vechain_requestAccounts',
                    'vechain_getAccounts'
                  ];
                  
                  for (const method of methods) {
                    try {
                                            
                      // 🚀 Für verschiedene Methoden unterschiedliche Parameter
                      let params = {};
                      if (method.includes('sendTransaction') || method.includes('signTransaction')) {
                        params = {
                          to: transactionData.to,
                          value: transactionData.value,
                          data: '0x'
                        };
                      } else if (method.includes('Accounts')) {
                        params = [];
                      }
                      
                      const result = await provider.request({
                        method: method,
                        params: params
                      });
                      
                                            return { 
                        method: 'direct', 
                        success: true, 
                        transaction: result,
                        message: `Transaktion über ${method} gestartet!`
                      };
                      
                    } catch (methodError) {
                                            continue;
                    }
                  }
                  
                } catch (requestError) {
                                  }
              }
              
            } catch (apiError) {
                          }
            
          } catch (extensionError) {
                        continue;
          }
        }
        
              } else {
              }
      
      // 🎯 2. PRIORITÄT: WalletConnect v2 + VeChain DApp Kit (Mobile)
            
      try {
        // 🚀 WICHTIG: Wir verwenden die bereits bestehende Session aus der v3tax-App!
        // KEINE neue WalletConnect-Session erstellen!
        
        // 🎯 Hole die bereits eingeloggte Wallet aus dem Auth-Context
        const userWallet = transactionData.userWallet || '0xe9659458997320dfa1e52a5b5d7b17822920d644';
        
                
        // 🚀 JETZT: Echte Transaktion erstellen und zur Unterschrift senden!
                
        // 🎯 WICHTIG: Diese API wird nicht mehr für die Transaktion verwendet
        // Die Transaktion wird jetzt direkt in der React-Komponente mit den Hooks ausgeführt
        throw new Error('Transaktion wird direkt in der React-Komponente ausgeführt');
        
      } catch (error) {
                
        // 🎯 Fallback auf QR-Code, falls Transaktion fehlschlägt
        if (error.message.includes('User rejected')) {
          throw new Error('Transaktion vom User in VeWorld App abgelehnt');
        }
        
        return {
          method: 'qrCode',
          success: true,
          message: 'Transaktion fehlgeschlagen - bitte verbinde manuell',
          instructions: 'Öffne VeWorld App → WalletConnect → QR-Code scannen'
        };
      }
      
      // 🎯 3. FALLBACK: Manuelle Anleitung
            
      return {
        method: 'manual',
        success: false,
        instructions: {
          de: `Manuelle Zahlung: Sende genau ${transactionData.value} ${transactionData.tokenSymbol} an ${transactionData.to}`,
          en: `Manual payment: Send exactly ${transactionData.value} ${transactionData.tokenSymbol} to ${transactionData.to}`
        },
        details: {
          to: transactionData.to,
          amount: transactionData.value,
          token: transactionData.tokenSymbol,
          plan: transactionData.plan,
          price: transactionData.priceEUR
        }
      };
      
    } catch (error) {
            throw error;
    }
  }
};

// 🧾 NEU: Invoice API Functions
export async function fetchInvoices(userWallet, year = null, plan = null) {
  try {
    let url = apiUrl(`/api/invoices`);
    const params = new URLSearchParams();
    
    if (year) params.append('year', year);
    if (plan) params.append('plan', plan);
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    const response = await fetch(url, {
      credentials: 'include',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token') || 'demo-token'}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
        throw error;
  }
}

export async function downloadInvoicePdfByPurchaseId(purchaseId, language = 'de') {
  try {
    const response = await fetch(apiUrl(`/api/invoices/by-purchase/${purchaseId}/pdf?lang=${encodeURIComponent(language)}`), {
      credentials: 'include',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token') || 'demo-token'}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    // PDF-Datei herunterladen
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = (language === 'en')
      ? `invoice_${purchaseId}.pdf`
      : `Rechnung_${purchaseId}.pdf`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    
      } catch (error) {
        throw error;
  }
}

export async function downloadInvoicePdf(invoiceId) {
  try {
    const response = await fetch(apiUrl(`/api/invoices/${invoiceId}/pdf`), {
      credentials: 'include',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token') || 'demo-token'}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    // Create blob and download
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoice-${invoiceId}.pdf`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    
    return { success: true };
  } catch (error) {
        throw error;
  }
}

// ── B2B Invoice Center (Admin) ─────────────────────────────────────────────

async function b2bJson(url, options = {}) {
  const response = await fetch(apiUrl(url), {
    credentials: 'include',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });

  const json = await response.json().catch(() => ({}));

  if (!response.ok || json?.success === false) {
    const error = new Error(
      json?.error || `B2B API failed (${response.status})`
    );
    error.status = response.status;
    error.body = json;
    throw error;
  }

  return json;
}

export async function fetchB2BClients() {
  return b2bJson('/api/b2b/clients');
}

export async function createB2BClient(payload) {
  return b2bJson('/api/b2b/clients', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateB2BClient(id, payload) {
  return b2bJson(`/api/b2b/clients/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function fetchB2BInvoices(filters = {}) {
  const params = new URLSearchParams();

  if (filters.clientId) {
    params.set('clientId', filters.clientId);
  }

  if (filters.status) {
    params.set('status', filters.status);
  }

  const qs = params.toString();

  return b2bJson(`/api/b2b/invoices${qs ? `?${qs}` : ''}`);
}

export async function createB2BInvoice(payload) {
  return b2bJson('/api/b2b/invoices', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateB2BInvoice(id, payload) {
  return b2bJson(`/api/b2b/invoices/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function issueB2BInvoice(id) {
  return b2bJson(`/api/b2b/invoices/${id}/issue`, {
    method: 'POST',
  });
}

export async function markB2BInvoicePaid(id) {
  return b2bJson(`/api/b2b/invoices/${id}/mark-paid`, {
    method: 'POST',
  });
}

export async function deleteB2BDraft(id) {
  return b2bJson(`/api/b2b/invoices/${id}`, {
    method: 'DELETE',
  });
}

export async function openB2BInvoicePDF(id, preview = false) {
  const endpoint = preview
    ? `/api/b2b/invoices/${id}/preview-pdf`
    : `/api/b2b/invoices/${id}/pdf`;

  const response = await fetch(apiUrl(endpoint), {
    credentials: 'include',
  });

  const contentType = String(response.headers.get('content-type') || '');

  if (!response.ok) {
    let details = '';
    try {
      const json = await response.json();
      details = json?.error || json?.details || '';
    } catch {
      // ignore
    }
    throw new Error(
      details || `PDF konnte nicht geladen werden (${response.status})`
    );
  }

  const raw = await response.arrayBuffer();
  const bytes = new Uint8Array(raw);
  const header = String.fromCharCode(
    bytes[0] || 0,
    bytes[1] || 0,
    bytes[2] || 0,
    bytes[3] || 0
  );

  if (!bytes.length || header !== '%PDF') {
    const maybeText = new TextDecoder().decode(bytes.slice(0, 300));
    throw new Error(
      `Ungültige PDF-Antwort vom Server. ${maybeText.slice(0, 120)}`
    );
  }

  const pdfBlob = new Blob([bytes], { type: 'application/pdf' });
  const blobUrl = URL.createObjectURL(pdfBlob);
  const filename = preview
    ? `b2b-entwurf-${id}.pdf`
    : `b2b-rechnung-${id}.pdf`;

  // Wie B2C: als Datei öffnen/downloaden — zuverlässiger als blob:-Tab in Edge
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  // Zusätzlich Versuch, im Tab zu öffnen (Chrome o.ä.)
  window.open(blobUrl, '_blank', 'noopener,noreferrer');

  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}

// 🚀 NEU: createPendingOrder API-Funktion für VeWorld-Integration mit IP-Guard
export async function createPendingOrder({ plan, year, walletCount, priceEUR, preferredToken, paymentAmounts, walletAddresses, residenceCountry }) {
  const res = await fetch('/api/veworld/create-pending-order', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      plan, year, walletCount, priceEUR, preferredToken,
      paymentAmounts, walletAddresses, residenceCountry
    })
  });

  if (res.status === 422) {
    const err = await res.json().catch(() => ({}));
    const e = new Error(err.message || 'Nachweis erforderlich.');
    e.code = err.code || 'NEEDS_IP_EVIDENCE';
    throw e;
  }
  if (!res.ok) {
    const t = await res.text();
    throw new Error(`Pending fehlgeschlagen: ${res.status} ${t}`);
  }
  return res.json(); // { success:true, paymentId }
}

// 🚀 FIX: Start Pending-Order API-Funktion
export async function startPendingOrder({ paymentId }) {
  const res = await fetch('/api/veworld/start-pending-order', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ paymentId })
  });

  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || 'Pending-Order konnte nicht gestartet werden');
  }

  return await res.json();
}

// 🚀 FIX: Cancel Pending-Order API-Funktion
export async function cancelPendingOrder({ paymentId }) {
  const res = await fetch('/api/veworld/cancel-pending-order', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ paymentId })
  });

  if (!res.ok) {
    const error = await res.json();
    throw new Error(error.error || 'Pending-Order konnte nicht gecancelt werden');
  }

  return await res.json();
}

export async function trackPlatformActivity(eventType, metadata = {}) {
  try {
    const source = metadata?.source || null;
    const wallet = String(metadata?.walletAddress || "").toLowerCase();
    const dedupeKey = `v3tax:activity:${eventType}:${wallet || "anon"}:${source || "unknown"}`;
    const dedupeMs = eventType === "login" ? 60_000 : 30_000;
    const now = Date.now();

    try {
      const last = Number(sessionStorage.getItem(dedupeKey) || 0);
      if (last && now - last < dedupeMs) {
        return { success: true, skipped: true };
      }
      sessionStorage.setItem(dedupeKey, String(now));
    } catch {}

    const response = await fetch(apiUrl("/api/platform-activity/track"), {
      method: "POST",
      credentials: "include",
      cache: "no-store",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        eventType,
        source,
        year: metadata?.year || metadata?.tax_year || null,
        datasetId: metadata?.datasetId || metadata?.dataset_id || null,
        purchaseId: metadata?.purchaseId || metadata?.purchase_id || null,
        selectedCountry: metadata?.selectedCountry || metadata?.country || null,
        walletSet: metadata?.walletSet || null,
        metadata,
      }),
    });

    const json = await response.json().catch(() => ({}));

    if (!response.ok) {
      return {
        success: false,
        status: response.status,
        error: json?.error || "track_failed",
      };
    }

    return json;
  } catch {
    return { success: false, error: "track_failed" };
  }
}

export async function fetchAdminActivityOverview(options = {}) {
  const params = new URLSearchParams();

  if (typeof options === "number") {
    params.set("days", String(options));
  } else {
    const {
      days = 30,
      from,
      to,
      granularity,
      eventType,
      wallet,
      limit = 200,
      offset = 0,
    } = options || {};

    if (days != null) params.set("days", String(days));
    if (from) params.set("from", String(from));
    if (to) params.set("to", String(to));
    if (granularity) params.set("granularity", String(granularity));
    if (eventType) params.set("eventType", String(eventType));
    if (wallet) params.set("wallet", String(wallet));
    if (limit != null) params.set("limit", String(limit));
    if (offset != null) params.set("offset", String(offset));
  }

  const response = await fetch(
    apiUrl(`/api/platform-activity/admin/overview?${params.toString()}`),
    {
      credentials: "include",
      cache: "no-store",
    }
  );

  const json = await response.json().catch(() => ({}));

  if (!response.ok || !json?.success) {
    const err = new Error(
      json?.error || `Admin activity failed (${response.status})`
    );
    err.status = response.status;
    err.body = json;
    throw err;
  }

  return json;
}

export async function prepareAdminReplayFromActivityEvent(event = {}) {
  const response = await fetch(apiUrl("/api/admin/replay/prepare"), {
    method: "POST",
    credentials: "include",
    cache: "no-store",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      activityEventId: event.id,
      sourceDatasetId: event.dataset_id ?? null,
      year: event.tax_year ?? null,
      selectedCountry: event.selected_country ?? null,
      isPreview: !!event.is_preview,
      userWallet: event.user_wallet ?? null,
    }),
  });

  const json = await response.json().catch(() => ({}));

  if (!response.ok || !json?.success) {
    const err = new Error(
      json?.error || `Admin replay prepare failed (${response.status})`
    );
    err.status = response.status;
    err.body = json;
    throw err;
  }

  return json;
}